import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import SecurityPicker, { PickedSecurity } from './shared/SecurityPicker';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  DockTitle,
  EconomicsPreview,
  InstrumentGrid,
  ScopePill,
  SearchDock,
  StructuredBand,
  StructuredContextPanel,
  StructuredHeroPanel,
  StructuredWorkspace,
  TradeEconomicsGrid,
  type StructuredContextRow,
} from './shared/StructuredCreditTicketLayout';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import { BadgeDollarSign, CalendarDays, PackageOpen, Landmark, Layers, Search, ShieldCheck, Users } from 'lucide-react';

import { DateField } from '../shared/DateField';

const fmtAmount = (value: string) => {
  const parsed = parseAmountShorthand(value);
  return parsed > 0 ? formatAmountWithCommas(parsed) : value;
};

const num = (value: any, fallback = 0) => {
  const n = value === '' || value == null ? fallback : parseAmountShorthand(String(value));
  return Number.isFinite(n) ? n : fallback;
};

const rowsFrom = (rows: Array<[string, any, boolean?]>): StructuredContextRow[] =>
  rows
    .filter(([, value]) => value != null && value !== '')
    .map(([label, value, accent]) => ({ label, value: String(value), accent }));

// ADR 0009: an ABS tranche trades and settles on SIFMA (its currency's calendar outside dollars),
// at T+1, the convention the server applies to an ABS ticket. The server has the last word.
const TICKET_TYPE = 'ABS_TRANCHE';
const SETTLE_CONVENTION = 'T+1';

const settleQuery = (tradeDate: string, currency?: string) =>
  ({ tradeDate, ticketType: TICKET_TYPE, convention: SETTLE_CONVENTION, currency: currency || 'USD' });

/** The provisional settle date at once, then the server's, unless the form has moved on meanwhile. */
const syncSettleDate = (setFormData: React.Dispatch<React.SetStateAction<any>>, tradeDate: string, currency?: string) => {
  const query = settleQuery(tradeDate, currency);
  const provisional = provisionalSettleDate(query);
  setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
  void resolveSettleDate(query).then(settleDate => setFormData((prev: any) =>
    prev.tradeDate === tradeDate && (prev.currency || 'USD') === query.currency && prev.settleDate === provisional
      ? { ...prev, settleDate } : prev));
};

/** Runs once when a new ticket opens. */
const OnOpen: React.FC<{ run: () => void }> = ({ run }) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);
  return null;
};

const AbsDealContext: React.FC<{ securityId?: number | null }> = ({ securityId }) => {
  const [detail, setDetail] = useState<any | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!securityId) {
      setDetail(null);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`/api/credit/abs/securities/${securityId}`);
        if (!cancelled) setDetail(data || null);
      } catch {
        if (!cancelled) setDetail(null);
      }
    })();
    return () => { cancelled = true; };
  }, [securityId]);

  const rows = detail ? rowsFrom([
    ['Deal', detail.dealName, true],
    ['Sector', detail.sector],
    ['Trustee', detail.trusteeName],
    ['Tranche', detail.trancheName, true],
    ['Class', detail.trancheClass],
    ['Priority', detail.paymentPriority],
    ['Coupon', detail.couponRate != null ? `${detail.couponRate}%` : null],
    ['Factor', detail.currentFactor],
    ['Credit Support', detail.currentCreditSupportPct != null ? `${detail.currentCreditSupportPct}%` : null, true],
    ['WAL', detail.expectedWalYears != null ? `${detail.expectedWalYears} yrs` : null],
    ['Rating', detail.rating],
  ]) : [];

  return (
    <StructuredContextPanel
      toneName="abs"
      icon={Layers}
      title="Deal And Tranche Snapshot"
      subtitle={detail?.dealName || 'Consumer and commercial receivables collateral profile'}
      rows={rows}
      emptyTitle={securityId ? 'Deal detail unavailable' : 'No tranche selected'}
      emptyText={securityId ? 'The selected security has no ABS deal snapshot.' : 'Select a tranche to show sector, factor, support, WAL, and rating.'}
    />
  );
};

const ABSTrancheTicketScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // A trade opened from the blotter keeps its stored settle date.
  const openingTrade = !!((location.state as any)?.transactionId || (location.state as any)?.id);
  const openTradeDate = getTodayDate(calendarsFor(TICKET_TYPE, 'USD'));

  const initialFormData = {
    transactionType: 'BUY' as const,
    assetManager: '',
    portfolioManager: '',
    assetClass: 'ASSET_BACKED_SECURITIES',
    assetSubClass: 'ABS Tranche',
    portfolio: '',
    tradeDate: openTradeDate,
    settleDate: provisionalSettleDate(settleQuery(openTradeDate)),
    submittedBy: getCurrentOperatorName(),
    currency: 'USD',
    executedBy: '',
    notes: '',
    securityId: '' as any,
    cusip: '',
    security: '',
    facePurchased: '',
    price: '99.5',
  };

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    const isCreate = action === 'draft' || action === 'submitForPreClear';
    if (isCreate) {
      if (!formData.securityId) errors.securityId = 'Select an ABS tranche';
      if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
      if (num(formData.facePurchased) <= 0) errors.facePurchased = 'Face purchased must be > 0';
      if (num(formData.price) <= 0) errors.price = 'Price must be > 0';
      if (!formData.tradeDate) errors.tradeDate = 'Trade date is required';
    }
    if (action === 'execute' && !formData.executedBy?.trim()) errors.executedBy = 'Executed By is required';
    return errors;
  };

  const loadTransactionData = (transaction: any) => {
    const str = (v: any) => (v == null ? '' : v.toString());
    return {
      ...initialFormData,
      transactionType: transaction.transactionType || 'BUY',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      securityId: transaction.securityId || '',
      cusip: transaction.cusip || '',
      security: transaction.securityName || transaction.cusip || '',
      facePurchased: str(transaction.amount != null ? transaction.amount : transaction.quantity),
      price: transaction.price != null ? transaction.price.toString() : '',
      tradeDate: transaction.tradeDate || getTodayDate(),
      settleDate: transaction.settleDate || getTodayDate(),
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      currency: transaction.currency || 'USD',
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
    };
  };

  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find(
      (p: any) => p.portfolioName === formData.portfolio.trim()
    );
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);
    if (!formData.securityId) throw new Error('No ABS instrument selected');

    let assetManagerId: number | null = null;
    if (formData.assetManager?.trim()) {
      const am = dimensionData.assetManagers.find((a: any) => a.assetManagerName === formData.assetManager.trim());
      if (am?.id) assetManagerId = am.id;
    }
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager?.trim()) {
      const pm = dimensionData.portfolioManagers.find((p: any) => p.portfolioManagerName === formData.portfolioManager.trim());
      if (pm?.id) portfolioManagerId = pm.id;
    }

    const face = num(formData.facePurchased);
    const price = num(formData.price, 100);

    return {
      transactionType: 'BUY',
      securityId: Number(formData.securityId),
      portfolioId: portfolio.id,
      assetManagerId,
      portfolioManagerId,
      quantity: face,
      price,
      amount: face,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate || undefined,
      notes: formData.notes || `ABS tranche purchase: ${formData.security || formData.cusip}`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'ABS_TRANCHE',
      eventType: 'ACQUIRE',
      currency: formData.currency || 'USD',
      cusip: formData.cusip,
    };
  };

  return (
    <BaseTicketScreen
      title="ABS Tranche Trade Entry"
      assetClass="ASSET_BACKED_SECURITIES"
      assetSubClass="ABS Tranche"
      hidePortfolioField
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange }) => {
        const pickerLocked = isFieldDisabled('securityId') || (!!transactionStatus && transactionStatus !== 'DRAFT');
        const selected: PickedSecurity | null = formData.securityId
          ? { id: Number(formData.securityId), cusip: formData.cusip, securityName: formData.security }
          : null;
        const face = num(formData.facePurchased);
        const price = num(formData.price, 100);
        const consideration = face * price / 100;

        return (
          <StructuredWorkspace>
            {!openingTrade && <OnOpen run={() => syncSettleDate(setFormData, formData.tradeDate, formData.currency)} />}
            <StructuredHeroPanel
              toneName="abs"
              icon={PackageOpen}
              eyebrow="Structured Credit"
              title="ABS Tranche"
              description="Consumer and commercial receivables exposure with tranche-level credit support, WAL, and rating context alongside the trade economics."
              badges={['ABS', 'Face x price', 'IBOR posted']}
              signals={[
                { label: 'Selected', value: formData.security || 'No tranche', accent: !!formData.security },
                { label: 'CUSIP', value: formData.cusip || '-' },
                { label: 'Trade Unit', value: 'Face amount' },
                { label: 'Currency', value: formData.currency || 'USD' },
              ]}
            />

            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="abs"><Search size={14} /> Instrument</BandEyebrow>
                  <BandTitle>Tranche Selection</BandTitle>
                  <BandSubline>Security master selection with read-only ABS deal context.</BandSubline>
                </BandTitleGroup>
                <ScopePill $tone="abs">Receivables Collateral</ScopePill>
              </BandHeader>
              <InstrumentGrid>
                <SearchDock $tone="abs">
                  <DockTitle><ShieldCheck size={16} /> Approved Instrument</DockTitle>
                  <SecurityPicker
                    assetClass="ASSET_BACKED_SECURITIES"
                    label="ABS Tranche (CUSIP)*"
                    placeholder="Search by deal, tranche, or CUSIP..."
                    selected={selected}
                    disabled={pickerLocked}
                    error={errors.securityId}
                    notFoundLabel="Define new ABS deal/tranche"
                    onDefineNew={() => navigate('/reference-data?tab=structured&assetClass=ABS&new=1')}
                    onSelect={(sec) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        securityId: sec.id,
                        cusip: sec.cusip || '',
                        security: sec.securityName || sec.cusip || '',
                        currency: sec.currency || prev.currency,
                      }));
                      // Another currency settles on another calendar.
                      if (sec.currency && sec.currency !== formData.currency) syncSettleDate(setFormData, formData.tradeDate, sec.currency);
                    }}
                    onClear={() => setFormData((prev: any) => ({ ...prev, securityId: '', cusip: '', security: '' }))}
                  />
                </SearchDock>
                <AbsDealContext securityId={formData.securityId ? Number(formData.securityId) : null} />
              </InstrumentGrid>
            </StructuredBand>

            <TradeEconomicsGrid>
              <StructuredBand>
                <BandHeader>
                  <BandTitleGroup>
                    <BandEyebrow $tone="abs"><CalendarDays size={14} /> Trade Setup</BandEyebrow>
                    <BandTitle>Portfolio, Dates, And Price</BandTitle>
                    <BandSubline>Drafted as an acquisition trade and routed through the standard lifecycle.</BandSubline>
                  </BandTitleGroup>
                  <ScopePill $tone="abs">BUY</ScopePill>
                </BandHeader>

                <FormRow>
                  <FormGroup>
                    <Label>Portfolio*</Label>
                    <Select value={formData.portfolio} onChange={(e) => handleInputChange('portfolio', e.target.value)} className={errors.portfolio ? 'error' : ''} disabled={isFieldDisabled('portfolio')}>
                      <option value="">Select Portfolio</option>
                      {dimensionData.portfolios.map((p: any) => (<option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>))}
                    </Select>
                    {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label><Landmark size={13} /> Asset Manager</Label>
                    <Select value={formData.assetManager} onChange={(e) => handleInputChange('assetManager', e.target.value)} disabled={isFieldDisabled('assetManager')}>
                      <option value="">Select</option>
                      {dimensionData.assetManagers.map((am: any) => (<option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label><Users size={13} /> Portfolio Manager</Label>
                    <Select value={formData.portfolioManager} onChange={(e) => handleInputChange('portfolioManager', e.target.value)} disabled={isFieldDisabled('portfolioManager')}>
                      <option value="">Select</option>
                      {dimensionData.portfolioManagers.map((pm: any) => (<option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>))}
                    </Select>
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Trade Date*</Label>
                    <DateField value={formData.tradeDate ?? ''} className={errors.tradeDate ? 'error' : ''} disabled={isFieldDisabled('tradeDate')} calendars={calendarsFor(TICKET_TYPE, formData.currency)} onChange={iso => { handleInputChange('tradeDate', iso); syncSettleDate(setFormData, iso, formData.currency); }} />
                    {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Settle Date</Label>
                    <DateField value={formData.settleDate ?? ''} disabled={isFieldDisabled('settleDate')} calendars={settlementCalendars({ ticketType: TICKET_TYPE, currency: formData.currency })} onChange={iso => handleInputChange('settleDate', iso)} />
                  </FormGroup>
                  <FormGroup>
                    <Label>Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
                    <Select value={formData.executedBy} onChange={(e) => handleInputChange('executedBy', e.target.value)} disabled={isFieldDisabled('executedBy')} className={errors.executedBy ? 'error' : ''}>
                      <option value="">Select</option>
                      {dimensionData.users.map((u: any) => (<option key={u.id} value={u.fullName}>{u.fullName}{u.role ? ` (${u.role})` : ''}</option>))}
                    </Select>
                    {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label><BadgeDollarSign size={13} /> Face Purchased*</Label>
                    <Input value={formData.facePurchased} onChange={(e) => handleInputChange('facePurchased', e.target.value)} onBlur={(e) => setFormData((p: any) => ({ ...p, facePurchased: fmtAmount(e.target.value) }))} className={errors.facePurchased ? 'error' : ''} disabled={isFieldDisabled('facePurchased')} />
                    {errors.facePurchased && <FieldError>{errors.facePurchased}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Price (% of par)*</Label>
                    <Input type="number" step="0.01" value={formData.price} onChange={(e) => handleInputChange('price', e.target.value)} className={errors.price ? 'error' : ''} disabled={isFieldDisabled('price')} />
                    {errors.price && <FieldError>{errors.price}</FieldError>}
                  </FormGroup>
                </FormRow>
              </StructuredBand>

              <EconomicsPreview
                toneName="abs"
                title="Live Economics"
                notionalLabel="Face Purchased"
                notionalValue={face}
                price={price}
                consideration={consideration}
                currency={formData.currency || 'USD'}
                extraMetrics={[
                  { label: 'Settlement', value: formData.settleDate || '-' },
                  { label: 'Posting', value: 'Par x price', accent: true },
                ]}
              />
            </TradeEconomicsGrid>
          </StructuredWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default ABSTrancheTicketScreen;
